import { useState } from "react";
import { TemplateCsvImportDialog } from "../../components/template/TemplateCsvImportDialog";
import { TemplateItemsViewTable } from "../../components/template/TemplateItemsViewTable";
import { Button } from "../../components/ui/button";
import type { TemplatePreset } from "../../hooks";
import {
  useTemplatePresetItems,
  useTemplatePresetMutations,
  useTemplatePresets,
} from "../../hooks";
import { TemplatePresetFormDialog } from "./components/TemplatePresetFormDialog";

// 閲覧者のタイムゾーンによらず JST の日付を出す。日付だけを見せる列なので、
// ローカル時刻で成分を取り出すと 00:00+09:00 前後の更新が前日に見える
const dateFormatter = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function formatDate(dateString: string | null) {
  if (!dateString) return "-";
  return dateFormatter.format(new Date(dateString));
}

export function TemplatePresets() {
  const { templatePresets, loading, error, refetch } = useTemplatePresets();
  const { deleteTemplatePreset, error: deleteError } =
    useTemplatePresetMutations();
  // 削除の失敗が入稿ダイアログのエラー表示に混ざらないよう、用途ごとに state を分ける
  const {
    bulkCreateTemplatePresetItems,
    loading: importLoading,
    error: importError,
  } = useTemplatePresetMutations();

  const [selectedPreset, setSelectedPreset] = useState<TemplatePreset | null>(
    null,
  );
  const [formTarget, setFormTarget] = useState<TemplatePreset | "new" | null>(
    null,
  );
  const [importTarget, setImportTarget] = useState<TemplatePreset | null>(null);

  const {
    templatePresetItems,
    loading: itemsLoading,
    refetch: refetchItems,
  } = useTemplatePresetItems(selectedPreset?.templatePresetId ?? null);

  const handleDelete = async (preset: TemplatePreset) => {
    if (
      !confirm(
        `「${preset.name}」を削除しますか？\n取り込み済みのタームの設問には影響しません。`,
      )
    ) {
      return;
    }
    const deleted = await deleteTemplatePreset(preset.templatePresetId);
    // 失敗時のメッセージは hook の state に入る。alert に渡すと再レンダリング前の
    // 古い値を読むため、画面に出す
    if (!deleted) return;
    if (selectedPreset?.templatePresetId === preset.templatePresetId) {
      setSelectedPreset(null);
    }
    refetch();
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-danger)]">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="mx-auto max-w-[1000px]">
        <div className="mb-8 flex items-start justify-between">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              テンプレートスクリプト
            </h1>
            <p className="font-medium text-[var(--color-text-secondary)] text-sm">
              タームの設問テンプレートに取り込めるスクリプトを管理します
            </p>
          </div>
          <Button onClick={() => setFormTarget("new")}>新規作成</Button>
        </div>

        {deleteError && (
          <div className="mb-4 rounded-lg bg-[var(--color-red-50)] p-3 text-[var(--color-text-danger)] text-sm">
            {deleteError}
          </div>
        )}

        <div className="mb-6 rounded-xl bg-white p-6">
          {templatePresets.length === 0 ? (
            <p className="py-10 text-center text-[var(--color-text-secondary)] text-sm">
              テンプレートスクリプトがまだ登録されていません
            </p>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--color-bg-subtle-gray)]">
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    名前
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    メモ
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    項目数
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    更新日
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {templatePresets.map((preset) => (
                  <tr
                    key={preset.templatePresetId}
                    className="border-[var(--color-border-default)] border-b"
                  >
                    <td className="p-3 font-medium text-sm">{preset.name}</td>
                    <td className="p-3 text-[var(--color-text-secondary)] text-sm">
                      {preset.description || "-"}
                    </td>
                    <td className="p-3 text-[var(--color-text-secondary)] text-sm">
                      {preset.itemCount}件
                    </td>
                    <td className="p-3 text-[var(--color-text-secondary)] text-sm">
                      {formatDate(preset.updatedAt)}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedPreset(preset)}
                        >
                          項目を見る
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setImportTarget(preset)}
                        >
                          項目を入稿
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setFormTarget(preset)}
                        >
                          編集
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(preset)}
                        >
                          削除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedPreset && (
          <div className="rounded-xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-bold text-lg">{selectedPreset.name}</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedPreset(null)}
              >
                閉じる
              </Button>
            </div>
            {itemsLoading ? (
              <p className="py-10 text-center text-[var(--color-text-secondary)] text-sm">
                読み込み中...
              </p>
            ) : (
              <TemplateItemsViewTable
                items={templatePresetItems.map((item) => ({
                  key: item.templatePresetItemId,
                  category: item.category,
                  timeEstimate: item.timeEstimate,
                  itemName: item.itemName,
                  question: item.question,
                  note: item.note,
                  isRequired: item.isRequired,
                }))}
              />
            )}
          </div>
        )}
      </div>

      <TemplatePresetFormDialog
        open={formTarget !== null}
        preset={formTarget === "new" ? null : formTarget}
        onOpenChange={(open) => {
          if (!open) setFormTarget(null);
        }}
        onSaved={refetch}
      />

      <TemplateCsvImportDialog
        open={importTarget !== null}
        onOpenChange={(open) => {
          if (!open) setImportTarget(null);
        }}
        title={`「${importTarget?.name ?? ""}」の項目を入稿`}
        loading={importLoading}
        error={importError}
        onImport={async (items) => {
          if (!importTarget) return false;
          return bulkCreateTemplatePresetItems(
            importTarget.templatePresetId,
            items,
          );
        }}
        onImported={() => {
          refetch();
          if (selectedPreset) refetchItems();
        }}
      />
    </div>
  );
}
